import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, LogIn, UserPlus, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AuthPromptModal = ({ isOpen, onClose, message, forced = false }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleLogin = () => {
    if (!forced) onClose?.();
    navigate('/login');
  };

  const handleSignup = () => {
    if (!forced) onClose?.();
    navigate('/signup');
  };

  const handleBackdropClick = () => {
    if (forced) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-testid={forced ? 'auth-modal-forced' : 'auth-modal'}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Close button — hidden when forced */}
        {!forced && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            data-testid="auth-modal-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {forced
              ? (t('continueRequiresLogin') || 'Login to Continue')
              : (t('loginRequired') || 'Login Required')}
          </h2>
          <p className="text-green-100 text-sm">
            {message ||
              (forced
                ? (t('forcedAuthMessage') ||
                    "You've been browsing for a while — please login or sign up (free) to continue exploring.")
                : (t('loginToAccess') || 'Please login to access this feature'))}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-center text-gray-600 mb-6">
            {t('authPromptMessage') ||
              'Create an account or login to explore government schemes, save favorites, and get personalized recommendations.'}
          </p>

          {/* Login Button */}
          <Button
            onClick={handleLogin}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold rounded-xl flex items-center justify-center gap-2"
            data-testid="auth-modal-login-btn"
          >
            <LogIn className="w-5 h-5" />
            {t('login') || 'Login'}
          </Button>

          {/* Signup Button */}
          <Button
            onClick={handleSignup}
            variant="outline"
            className="w-full border-2 border-green-600 text-green-600 hover:bg-green-50 py-6 text-lg font-semibold rounded-xl flex items-center justify-center gap-2"
            data-testid="auth-modal-signup-btn"
          >
            <UserPlus className="w-5 h-5" />
            {t('signup') || 'Create Account'}
          </Button>

          {/* Guest note — hidden in forced mode */}
          {!forced && (
            <p className="text-center text-xs text-gray-400 mt-4">
              {t('guestNote') || 'You can browse the home page without an account'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPromptModal;
